/**
 * F61 — a tiny single-flight + TTL cache for client reads.
 *
 * Pure TypeScript (no DOM, React or React Native), so it is safe for every
 * bundler: Vite (portal/admin), Turbopack (marketing) and Metro (mobile).
 *
 *   - `get(key)` resolves a fresh cached value, joins an in-flight request for
 *     the same key, or starts one. N components asking at once cost 1 request.
 *   - A failed load is never cached: the next `get` retries.
 *   - `invalidate(key)` (or `invalidate()` for every key) drops the entry; a
 *     request already in flight when that happens still resolves for its own
 *     callers but is not stored, so the next `get` reloads.
 *   - `ttlMs: Infinity` keeps a value for the whole session (static lookups).
 */

export interface CachedLoaderOptions<K> {
  /** How long a loaded value stays fresh. `Infinity` = for the session. */
  readonly ttlMs: number;
  /** Map a key to its cache slot (default `String(key)`). */
  readonly keyOf?: (key: K) => string;
  /** Clock seam for tests (default `Date.now`). */
  readonly now?: () => number;
}

export interface CachedLoader<K, V> {
  get(key: K): Promise<V>;
  /** The fresh cached value, if any — for a synchronous first render. */
  peek(key: K): V | undefined;
  invalidate(key?: K): void;
}

interface Entry<V> {
  readonly value: V;
  readonly at: number;
}

export function createCachedLoader<K, V>(
  load: (key: K) => Promise<V>,
  options: CachedLoaderOptions<K>,
): CachedLoader<K, V> {
  const keyOf = options.keyOf ?? ((key: K) => String(key));
  const now = options.now ?? Date.now;
  const values = new Map<string, Entry<V>>();
  const inFlight = new Map<string, Promise<V>>();
  // Bumped by invalidate(); a load only stores its value if its slot's
  // generation is unchanged when it settles.
  const generations = new Map<string, number>();
  let globalGeneration = 0;

  const generationOf = (slot: string): string =>
    `${globalGeneration}:${generations.get(slot) ?? 0}`;

  const fresh = (slot: string): Entry<V> | undefined => {
    const entry = values.get(slot);
    if (!entry) return undefined;
    if (now() - entry.at < options.ttlMs) return entry;
    values.delete(slot);
    return undefined;
  };

  return {
    get(key: K): Promise<V> {
      const slot = keyOf(key);
      const hit = fresh(slot);
      if (hit) return Promise.resolve(hit.value);
      const pending = inFlight.get(slot);
      if (pending) return pending;

      const startedGeneration = generationOf(slot);
      const request = load(key).then(
        (value) => {
          if (inFlight.get(slot) === request) inFlight.delete(slot);
          if (generationOf(slot) === startedGeneration) values.set(slot, { value, at: now() });
          return value;
        },
        (err: unknown) => {
          if (inFlight.get(slot) === request) inFlight.delete(slot);
          throw err;
        },
      );
      inFlight.set(slot, request);
      return request;
    },

    peek(key: K): V | undefined {
      return fresh(keyOf(key))?.value;
    },

    invalidate(key?: K): void {
      if (key === undefined) {
        globalGeneration += 1;
        values.clear();
        inFlight.clear();
        return;
      }
      const slot = keyOf(key);
      generations.set(slot, (generations.get(slot) ?? 0) + 1);
      values.delete(slot);
      inFlight.delete(slot);
    },
  };
}
